import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface AIConfig {
  id: string;
  provider: 'anthropic' | 'openai' | 'local';
  api_key?: string;
  api_url?: string;
  model_name?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export const useAIConfig = () => {
  const queryClient = useQueryClient();

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ['ai-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_config')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as AIConfig[];
    }
  });

  const { data: activeConfig } = useQuery({
    queryKey: ['ai-config-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_config')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as AIConfig | null;
    }
  });

  const createConfig = useMutation({
    mutationFn: async (config: Omit<AIConfig, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (config.is_active) {
        await supabase
          .from('ai_config')
          .update({ is_active: false })
          .neq('id', '00000000-0000-0000-0000-000000000000');
      }

      const { data, error } = await supabase
        .from('ai_config')
        .insert({
          ...config,
          created_by: user?.id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
      queryClient.invalidateQueries({ queryKey: ['ai-config-active'] });
      toast({
        title: 'Sucesso',
        description: 'Configuração de IA criada com sucesso'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao criar configuração',
        variant: 'destructive'
      });
    }
  });

  const updateConfig = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AIConfig> & { id: string }) => {
      if (updates.is_active) {
        await supabase
          .from('ai_config')
          .update({ is_active: false })
          .neq('id', id);
      }

      const { data, error } = await supabase
        .from('ai_config')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
      queryClient.invalidateQueries({ queryKey: ['ai-config-active'] });
      toast({
        title: 'Sucesso',
        description: 'Configuração atualizada com sucesso'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar configuração',
        variant: 'destructive'
      });
    }
  });

  const deleteConfig = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ai_config')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
      queryClient.invalidateQueries({ queryKey: ['ai-config-active'] });
      toast({
        title: 'Sucesso',
        description: 'Configuração excluída com sucesso'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao excluir configuração',
        variant: 'destructive'
      });
    }
  });

  const testConnection = useMutation({
    mutationFn: async (config: { provider: string; api_key?: string; api_url?: string; model_name?: string }) => {
      // Use server-side proxy instead of browser-side AI SDK
      const { data: result, error } = await supabase.functions.invoke('ai-proxy', {
        body: {
          action: 'test_connection',
          provider: config.provider,
          api_key: config.api_key,
          api_url: config.api_url,
          model_name: config.model_name,
        }
      });

      if (error) throw error;
      if (!result?.success) {
        throw new Error('Falha ao conectar com o serviço de IA');
      }

      return result.success;
    },
    onSuccess: () => {
      toast({
        title: 'Sucesso',
        description: 'Conexão testada com sucesso!'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro na Conexão',
        description: error.message || 'Não foi possível conectar ao serviço de IA',
        variant: 'destructive'
      });
    }
  });

  return {
    configs,
    activeConfig,
    isLoading,
    createConfig,
    updateConfig,
    deleteConfig,
    testConnection
  };
};
